import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Grid, Chip, Avatar, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { LocationOn as LocationOnIcon, Edit as EditIcon, Person as PersonIcon } from '@mui/icons-material';
import styles from './ExecutorMainInfoDesktop.module.css';
import { User, City, UserSkill, EmploymentStatus } from '../../../../types/user'; // Добавляем EmploymentStatus
import { Skill } from '../../../../types/skill';
import { userService } from '../../../../services/userService';
import { cityService } from '../../../../services/cityService';
import { skillService } from '../../../../services/skillService';

const calculateAge = (birthDateString?: string): string => {
    if (!birthDateString) {
        return "Не указана";
    }
    const birthDate = new Date(birthDateString);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return `${age} лет`;
};

const ExecutorMainInfoDesktop: React.FC = () => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedAboutMe, setEditedAboutMe] = useState<string>('');
    const [editedCityId, setEditedCityId] = useState<string | undefined>(undefined);
    const [cities, setCities] = useState<City[]>([]);
    const [citiesLoading, setCitiesLoading] = useState(true);
    const [citiesError, setCitiesError] = useState<string | null>(null);
    const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
    const [skillsLoading, setSkillsLoading] = useState(true);
    const [skillsError, setSkillsError] = useState<string | null>(null);
    const [editedSkills, setEditedSkills] = useState<string[]>([]);
    const [editedEmploymentStatus, setEditedEmploymentStatus] = useState<EmploymentStatus>(EmploymentStatus.Free); // Новое состояние для статуса занятости

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await userService.getMe();
                setUser(response.data);
                console.log('Fetched user data:', response.data);
                console.log('User employment status from API:', response.data.employmentStatus);
            } catch (err) {
                setError('Failed to fetch user data.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchUserData();
    }, []);

    useEffect(() => {
        const fetchCities = async () => {
            try {
                const response = await cityService.getAllCities();
                setCities(response.data);
            } catch (err) {
                setCitiesError('Failed to fetch cities.');
                console.error(err);
            } finally {
                setCitiesLoading(false);
            }
        };
        fetchCities();
    }, []);

    useEffect(() => {
        const fetchAvailableSkills = async () => {
            try {
                const response = await skillService.getAllSkills();
                setAvailableSkills(response.data);
            } catch (err) {
                setSkillsError('Failed to fetch skills.');
                console.error(err);
            } finally {
                setSkillsLoading(false);
            }
        };
        fetchAvailableSkills();
    }, []);

    if (loading || citiesLoading || skillsLoading) {
        return <Typography>Загрузка данных...</Typography>;
    }

    if (error || citiesError || skillsError) {
        return <Typography color="error">Ошибка: {error || citiesError || skillsError}</Typography>;
    }

    if (!user) {
        return <Typography>Данные пользователя не найдены.</Typography>;
    }

    // Динамические данные
    const fullName = user.fullName || "Не указано";
    const birthDate = user.birthDate ? new Date(user.birthDate).toLocaleDateString() : "Не указана";
    const age = calculateAge(user.birthDate); // Возраст будет вычисляться или приходить с бэкенда
    // const status = "Свободен"; // Теперь статус берется из user.employmentStatus
    const location = user.city?.name || "Не указан";
    const aboutMe = user.aboutMe || "Информация о себе не указана.";
    const skills = user.userSkills?.map((us: UserSkill) => us.skillTitle) || [];

    const handleEditToggle = async () => {
        if (isEditing) {
            // Save logic
            try {
                if (editedAboutMe !== user.aboutMe) {
                    await userService.updateAboutMe({ aboutMe: editedAboutMe });
                }
                if (editedCityId !== user.city?.id) {
                    if (editedCityId) {
                        await userService.changeCity({ cityId: editedCityId });
                    }
                }
                if (user.userSkills?.map((us: UserSkill) => us.skillId).sort().join(',') !== editedSkills.sort().join(',')) {
                    await userService.addSkills({ skillIds: editedSkills });
                }
                // Обновляем статус занятости, если он изменился
                if (editedEmploymentStatus !== user.employmentStatus) {
                    await userService.updateEmploymentStatus({ employmentStatus: editedEmploymentStatus });
                }
                // Refetch user data to get the latest updates
                const response = await userService.getMe();
                setUser(response.data);
                console.log('User data after save:', response.data);
                console.log('User employment status after save:', response.data.employmentStatus);
                setIsEditing(false);
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to save changes.');
                console.error(err);
            }
        } else {
            setEditedAboutMe(user.aboutMe || '');
            setEditedCityId(user.city?.id);
            setEditedSkills(user.userSkills?.map((us: UserSkill) => us.skillId) || []);
            setEditedEmploymentStatus(user.employmentStatus); // Инициализируем статус занятости для редактирования
            setIsEditing(true);
        }
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper elevation={3} sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 3 }} className={styles.profileHeader}>
                        <Avatar sx={{ width: 100, height: 100, bgcolor: '#7E57C2' }} className={styles.avatar}>
                            {/* Placeholder for user avatar */}
                            <PersonIcon sx={{ fontSize: 60 }} />
                        </Avatar>
                        <Box className={styles.userInfo}>
                            <Typography variant="h5"><b>{fullName}</b></Typography>
                            <Typography variant="body1" color="text.secondary">{birthDate} (возраст: {age})</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: user.employmentStatus === EmploymentStatus.Free ? 'success.main' : 'error.main', mr: 1 }} className={styles.statusIndicator} />
                                {isEditing ? (
                                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                                        <InputLabel>Статус</InputLabel>
                                        <Select
                                            value={editedEmploymentStatus}
                                            onChange={(e) => setEditedEmploymentStatus(e.target.value as EmploymentStatus)}
                                            label="Статус"
                                        >
                                            <MenuItem 
                                                value={EmploymentStatus.Free} 
                                                disabled={user.hasActiveTasks} // Отключаем, если у пользователя есть активные задачи
                                            >
                                                Свободен
                                            </MenuItem> 
                                            <MenuItem value={EmploymentStatus.Busy}>Занят</MenuItem>
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        {user.employmentStatus === EmploymentStatus.Free ? 'Свободен' : 'Занят'}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12}>
                    <Paper elevation={3} sx={{ p: 2.5 }} className={styles.detailsSection}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <LocationOnIcon color="action" sx={{ mr: 1 }} />
                                {isEditing ? (
                                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                                        <InputLabel>Город</InputLabel>
                                        <Select
                                            value={editedCityId || ''}
                                            onChange={(e) => setEditedCityId(e.target.value as string)}
                                            label="Город"
                                        >
                                            {cities.map((city) => (
                                                <MenuItem key={city.id} value={city.id}>
                                                    {city.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <Typography variant="body1">{location}</Typography>
                                )}
                            </Box>
                            <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEditToggle}>
                                {isEditing ? 'Сохранить' : 'Редактировать'}
                            </Button>
                        </Box>

                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Обо мне:</Typography>
                        {isEditing ? (
                            <TextField
                                label="Обо мне"
                                multiline
                                rows={4}
                                fullWidth
                                value={editedAboutMe}
                                onChange={(e) => setEditedAboutMe(e.target.value)}
                                variant="outlined"
                            />
                        ) : (
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{aboutMe}</Typography>
                        )}

                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Навыки:</Typography>
                        {isEditing ? (
                            <FormControl fullWidth variant="outlined" size="small">
                                <InputLabel>Навыки</InputLabel>
                                <Select
                                    multiple
                                    value={editedSkills}
                                    onChange={(e) => setEditedSkills(e.target.value as string[])}
                                    label="Навыки"
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => {
                                                const skill = availableSkills.find(s => s.id === value);
                                                return skill ? <Chip key={value} label={skill.title} /> : null;
                                            })}
                                        </Box>
                                    )}
                                >
                                    {availableSkills.map((skill: Skill, index: number) => (
                                        <MenuItem key={skill.id} value={skill.id}>
                                            {skill.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }} className={styles.skillsContainer}>
                                {skills.map((skill: string, index: number) => (
                                    <Chip key={index} label={skill} variant="outlined" />
                                ))}
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ExecutorMainInfoDesktop;
